import { useEffect, useState } from "react";

import { useParams } from "react-router-dom";

import { Button, CircularProgress } from "@material-ui/core";

import { getShipments, moveLtlShipmentToOOS } from "api/shipment";

import { ConfirmDialog } from "components/Custom";
import PageTitle from "components/PageTitle";

import { popupNotification, useLayoutDispatch } from "context/LayoutContext";
import { useUserState } from "context/UserContext";

import { initialAPIListValue } from "utils/constants";
import parseJwt from "utils/jwt";
import { getWithExpiry } from "utils/storage";

import ShipmentPreview from "./ShipmentPreview";

export default function LTLShipmentDetail() {
  const { shipment_id } = useParams();
  const layoutDispatch = useLayoutDispatch();
  const { permissions } = useUserState();
  const [shipment, setShipment] = useState(initialAPIListValue);
  const [oosConfirm, setOosConfirm] = useState(false);
  const [oosLoading, setOosLoading] = useState(false);

  const { is_manager } = parseJwt(getWithExpiry("access"));
  // Same audience and statuses the backend allows (Shipment.move_ltl_to_oos).
  const canMoveToOOS =
    (is_manager || permissions?.includes("contenttypes.shipment.cancel")) &&
    ["Open", "Picking", "Picked"].includes(shipment.status);

  useEffect(() => {
    getShipments({
      id: shipment_id,
      responseSetter: (data) => setShipment({ isFetching: false, ...data }),
    });
  }, [shipment_id]);

  const handleMoveToOOS = () => {
    // Picked units go back to their locations, so a second submit is not a harmless retry.
    if (oosLoading) return;
    setOosLoading(true);
    moveLtlShipmentToOOS({
      id: shipment.id,
      responseSetter: (res) => {
        const { success, message } = res;
        setOosLoading(false);
        setOosConfirm(false);
        if (!success) {
          return popupNotification({
            dispatch: layoutDispatch,
            message,
          });
        }
        popupNotification({
          dispatch: layoutDispatch,
          message: message || "Shipment moved to Out of Stock.",
          status: "success",
        });
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      },
    });
  };

  return shipment.isFetching ? (
    <CircularProgress />
  ) : (
    <>
      <PageTitle
        title={shipment.shipment_number}
        backLink="/app/ltl/shipments"
        button={
          canMoveToOOS && (
            <Button
              variant="contained"
              size="medium"
              color="secondary"
              disabled={oosLoading}
              onClick={() => setOosConfirm(true)}
            >
              Move to OOS
            </Button>
          )
        }
      />
      <ConfirmDialog
        title={`Move to Out of Stock (#${shipment.shipment_number})`}
        confirmText="Move to OOS"
        onClose={oosLoading ? null : () => setOosConfirm(false)}
        onConfirm={handleMoveToOOS}
        open={oosConfirm}
        isLoading={oosLoading}
      >
        This will move the whole shipment to Out of Stock. The order stays in
        the system and any picked items go back to the locations they came from.
        Re-Process it once the stock is in. Please confirm.
      </ConfirmDialog>
      <ShipmentPreview shipment={shipment} />
    </>
  );
}
